import AwesomeDebouncePromise from 'awesome-debounce-promise';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import flatMap from 'lodash/flatMap';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';

import type { PriceData, PriceQuery, PricesResponse } from 'chaika-frontend-types/frontend-cms';
import { Currency, PriceStatus, AuditEventType } from 'chaika-enums';
import type { Optional } from 'chaika-common-types';

import { ValidationParams } from '@components/types';

import { PriceApi } from '@api/PriceApi';

import { organizationService } from '@organization/service/organizaion/organizationService';
import { userService } from '@user/services/userService';
import { serviceService } from '@services/services/serviceService';
import { Price, PriceCreationParams, PriceUpdateParams } from '@store/models/Price/Price';

import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { DateHelper } from '@utils/DateHelper';

import { AbstractPriceStore } from './AbstractPriceStore';
import { auditLogService } from '@store/services/auditLogService';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { LegalEntityPriceListApi } from '@api/LegalEntityPriceListApi';
import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';

const findApi = AwesomeDebouncePromise(PriceApi.getPrices, 500);
const findApiPriceList = AwesomeDebouncePromise(LegalEntityPriceListApi.getPrices, 500);

export class AbstractPriceService {
    protected onLoadNext: boolean = false;
    protected priceStore_?: AbstractPriceStore;
    protected isCompositeServiceLoaded = false;
    private pricesForLegalEntity = new Map<string, PriceData[]>();
    private pricesLegalEntityIds = new Map<string, string[]>();
    private loadedLegalEntityServicePrises = new Set();

    constructor(priceStore?: AbstractPriceStore) {
        this.priceStore_ = priceStore;
    }

    public async load(): Promise<void> {
        const { limit } = this.priceStore.priceList;

        const [{ prices, count }] = await Promise.all([this.getPrices({ limit }), organizationService.load()]);

        await this.loadPriceRelatedData(prices);

        this.priceStore.priceList.set(prices);
        this.priceStore.priceList.count = count;
    }

    public async loadNext(
        stopIndex: number,
        saveToAllLists?: boolean,
        isLegalEntityPriceList?: boolean,
    ): Promise<void> {
        if (this.priceStore.searchList.searchParamsSet) {
            await this.loadNextSearchPrices(stopIndex, saveToAllLists, isLegalEntityPriceList);
        } else {
            await this.loadNextPrices(stopIndex, isLegalEntityPriceList);
        }
    }

    public async hasPricesForLegalEntityByOrganizationId(organizationId: string, serviceId: string): Promise<boolean> {
        const legalEntityId = store.legalEntityBranchList.getActualOnDayLegalEntityId(organizationId, new Date());
        const prices = !!legalEntityId ? await this.getPricesForLegalEntity(legalEntityId, serviceId) : [];

        return prices.length > 0;
    }

    public async getPricesForLegalEntity(legalEntityId: string, serviceId: string): Promise<PriceData[]> {
        const key = `${legalEntityId}:${serviceId}`;

        if (!this.pricesForLegalEntity.has(key)) {
            const { prices } = await PriceApi.getPrices({
                serviceId,
                legalEntityIds: legalEntityId,
                status: PriceStatus.Active,
                limit: 2,
            });

            this.pricesForLegalEntity.set(key, prices);
        }

        const prices = this.pricesForLegalEntity.get(key);
        assertIsDefined(prices, `Prices for legal entity key ${key} are loaded above`);
        return prices;
    }

    public async getPricesLegalEntityIds(serviceId: string): Promise<string[]> {
        if (!this.pricesLegalEntityIds.has(serviceId)) {
            const { prices } = await PriceApi.getPrices({
                serviceId,
                status: PriceStatus.Active,
                limit: 100,
            });

            this.pricesLegalEntityIds.set(serviceId, uniq(flatten(prices.map(({ legalEntityIds }) => legalEntityIds))));
        }

        const legalEntityIds = this.pricesLegalEntityIds.get(serviceId);
        assertIsDefined(legalEntityIds, `Legal entity ids of service ${serviceId} prices are loaded above`);
        return legalEntityIds;
    }

    public async loadPriceByIds(priceIds: string[]) {
        const newIds = getUniqNewIds(this.priceStore.priceList.ids, priceIds);
        if (newIds.length > 0) {
            const { prices } = await PriceApi.getPrices({
                limit: newIds.length,
                ids: newIds.join(','),
            });

            this.priceStore.priceList.add(prices);
        }
    }

    public async loadNotLoadedPricesByServiceIds(
        serviceIds: string[],
        legalEntityIds?: string[],
        status?: PriceStatus,
    ) {
        const serviceIdsToLoad: string[] = this.priceStore.priceList.getNotLoadedPriceServiceIds(uniq(serviceIds));

        await this.loadPriceByServiceIds(serviceIdsToLoad, legalEntityIds, status);
    }

    public async loadNotLoadedPruchasePricesByServiceIds(serviceIds: string[]) {
        const currentServiceIds = compact(this.priceStore.purchasePriceList.values.map(it => it.serviceId));
        const serviceIdsToLoad: string[] = getUniqNewIds(currentServiceIds, serviceIds);

        await this.loadPurchasePriceByServiceIds(serviceIdsToLoad);
    }

    public async loadPriceByServiceIds(
        serviceIds: string[],
        legalEntityIds?: string[],
        status?: PriceStatus,
    ): Promise<void> {
        const filteredIds = serviceIds.filter(serviceId => !this.priceStore.priceList.isPriceLoaded(serviceId));
        if (filteredIds.length > 0) {
            const chunkIds = getUniqChunkedIds(filteredIds);

            const priceParams = await Promise.all(
                chunkIds.map(async ids =>
                    PriceApi.getPrices({
                        status,
                        serviceId: ids.join(),
                        legalEntityIds: legalEntityIds?.join(),
                    }),
                ),
            );

            priceParams.forEach(({ prices }) => {
                this.priceStore.priceList.add(prices);
            });
        }
    }

    public async updateSearchParams(
        params: PriceQuery,
        saveToAllLists?: boolean,
        isLegalEntityPriceList?: boolean,
    ): Promise<void> {
        this.priceStore.searchList.setFilterParams(params);

        const { prices, count } = !!this.priceStore.searchParams
            ? isLegalEntityPriceList
                ? await findApiPriceList(this.getLegalEntityPriceListId(), this.priceStore.searchParams)
                : await findApi(this.priceStore.searchParams)
            : { prices: [], count: 0 };

        await this.loadPriceRelatedData(prices);

        this.priceStore.searchList.setResult(prices, count);

        if (saveToAllLists) {
            this.priceStore.priceList.addIfNotExist(prices);
        }
    }

    public async loadPricesByPriceList(
        legalEntityPriceListId: string,
        { serviceIds, legalEntityId }: { serviceIds: string[]; legalEntityId: Optional<string> },
    ): Promise<void> {
        const { priceList } = this.priceStore;
        const makeKey = (serviceId: string): string => {
            return `${legalEntityPriceListId}:${serviceId}:${legalEntityId}`;
        };

        const notLoadedServiceIds = serviceIds.filter(
            serviceId => !this.loadedLegalEntityServicePrises.has(makeKey(serviceId)),
        );
        if (notLoadedServiceIds.length > 0) {
            notLoadedServiceIds.forEach(serviceId => {
                this.loadedLegalEntityServicePrises.add(makeKey(serviceId));
            });
            const { prices } = await LegalEntityPriceListApi.getPrices(legalEntityPriceListId, {
                legalEntityIds: legalEntityId,
                serviceId: notLoadedServiceIds.join(','),
            });

            await this.loadPriceRelatedData(prices);

            priceList.addIfNotExist(prices);
        }
    }

    public async addPrice({ contractorId, ...createParams }: PriceCreationParams): Promise<void> {
        const hasContractor = isDefined(contractorId) && contractorId !== 'null';
        const params = hasContractor ? { contractorId, ...createParams } : createParams;
        const scheduledOn = DateHelper.getStartOfDay(params.scheduledOn).toISOString();

        await PriceApi.createPrice({ ...params, scheduledOn });

        const [{ prices }] = await Promise.all([
            PriceApi.getPrices(hasContractor ? { ids: params.id, contractorId } : { ids: params.id }),
            this.loadPriceRelatedData([{ includedServices: [], ...params }]),
        ]);

        const { priceList, searchList } = this.priceStore;
        const [price] = prices;
        if (searchList.searchParamsSet) {
            const searchValues = [...searchList.values.map((item: Price) => item.serialize()), price];
            searchList.setResult(searchValues, searchValues.length);
        }

        priceList.add([price]);
        priceList.count++;
    }

    public async updatePrice(priceId: string, params: PriceUpdateParams): Promise<void> {
        const { priceList, searchList } = this.priceStore;
        const updateParams = {
            ...params,
            scheduledOn: params.scheduledOn ? DateHelper.getStartOfDay(params.scheduledOn).toISOString() : undefined,
        };

        const price = priceList.get(priceId);
        const searchPrice = searchList.get(priceId);
        if (price) {
            price.update(updateParams);
        }
        if (searchPrice) {
            searchPrice.update(updateParams);
        }

        await PriceApi.updatePrice(priceId, updateParams);
        await auditLogService.logEvent({
            eventType: AuditEventType.PriceUpdate,
            entityId: priceId,
            data: {
                id: priceId,
                ...params,
            },
        });
    }

    public async archivePriceStatus(priceId: string, withoutCustomPrices?: boolean): Promise<void> {
        const price = this.priceStore.getPrice(priceId);
        assertIsDefined(price, `Price ${priceId} is archived from a loaded price list`);
        const priceInSearch = this.priceStore.getPriceFromSearchList(priceId);

        // @ts-ignore
        await Promise.all([
            PriceApi.updatePriceStatus(priceId, { status: PriceStatus.Archived }),
            !withoutCustomPrices &&
                price.isBasePrice() &&
                this.archiveCustomPrices(price.serviceId, price.legalEntityIds),
        ]);
        await auditLogService.logEvent({
            eventType: AuditEventType.PriceStatusUpdate,
            entityId: priceId,
            data: {
                id: priceId,
                status: PriceStatus.Archived,
            },
        });

        price.update({ status: PriceStatus.Archived });
        if (priceInSearch) {
            priceInSearch.update({ status: PriceStatus.Archived });
        }
    }

    public async deletePrice(priceId: string): Promise<void> {
        await PriceApi.deletePrice(priceId);

        this.priceStore.delete(priceId);
    }

    public async validateUpdatePriceParams(params: Partial<PriceCreationParams>): Promise<ValidationParams> {
        const { currency = Currency.RUB, legalEntityIds = [] } = params;
        const legalEntities = store.legalEntityList.getValuesByIds(legalEntityIds);
        return {
            isValid: legalEntities.every(legalEntity => legalEntity.currency === currency),
            message: translate('currencyMastMach'),
        };
    }

    public async validateCreatePriceParams(params: Partial<PriceCreationParams>): Promise<ValidationParams> {
        let validationParams = this.checkRequiredParams(params);

        const { serviceId, currency = Currency.RUB, legalEntityIds = [], userId, isComposite, value } = params;
        const legalEntities = store.legalEntityList.getValuesByIds(legalEntityIds);

        if (legalEntities.some(legalEntity => legalEntity.currency !== currency)) {
            validationParams = {
                isValid: false,
                message: translate('currencyMastMach'),
            };
        }

        if (serviceId && userId) {
            const checkBasePriceValidation = await this.checkBasePrice(serviceId, legalEntityIds);

            validationParams = {
                isValid: validationParams.isValid && checkBasePriceValidation.isValid,
                message: `${validationParams.message}${checkBasePriceValidation.message}`,
            };

            if (isComposite) {
                validationParams = {
                    isValid: false,
                    message: `${validationParams.message} ${translate('compositeServiceCanBeBase')}.`,
                };
            }
        } else if (serviceId) {
            validationParams = {
                isValid: true,
                message: ``,
            };
        }

        if (serviceId && userId) {
            const checkUserPriceValidation = this.checkUserPriceParams(params);

            validationParams = {
                isValid: validationParams.isValid && checkUserPriceValidation.isValid,
                message: `${validationParams.message}${checkUserPriceValidation.message}`,
            };
        }

        if (isComposite && (value?.kopecks ?? 0) > 0) {
            validationParams = {
                isValid: false,
                message: translate('mustHaveZeroPrice'),
            };
        }

        return validationParams;
    }

    public async loadCompositeServices(): Promise<void> {
        if (!this.isCompositeServiceLoaded) {
            await this.reloadCompositeServices();
        }
    }

    public async reloadCompositeServices(): Promise<void> {
        this.isCompositeServiceLoaded = true;
        const { prices } = await PriceApi.getCompositeServices();
        await Promise.all([
            this.loadPriceRelatedData(prices),
            this.loadPriceByServiceIds(flatMap(prices, 'includedServices')),
        ]);

        this.priceStore.compositeServiceList.set(prices);
    }

    public async updateCompositeServices(priceId: string, includedServices: string[]): Promise<void> {
        await Promise.all([
            PriceApi.updatePrice(priceId, { includedServices, isComposite: true }),
            serviceService.loadByIds(includedServices),
        ]);

        await auditLogService.logEvent({
            eventType: AuditEventType.PriceUpdate,
            entityId: priceId,
            data: {
                includedServices,
                id: priceId,
                isComposite: true,
            },
        });

        const compositeService = this.priceStore.compositeServiceList.getExisting(priceId);
        compositeService.includedServices = includedServices;
    }

    public async areCustomPricesForServiceExist(serviceId: string, legalEntityIds: string[]): Promise<boolean> {
        const customPrices = await this.getCustomPrices(serviceId, legalEntityIds);

        return customPrices.length > 0;
    }

    protected get priceStore(): AbstractPriceStore {
        if (!this.priceStore_) {
            this.priceStore_ = store.priceStore;
        }

        return this.priceStore_;
    }

    protected async getPrices(params: PriceQuery, isLegalEntityPriceList?: boolean): Promise<PricesResponse> {
        return isLegalEntityPriceList
            ? LegalEntityPriceListApi.getPrices(this.getLegalEntityPriceListId(), params)
            : PriceApi.getPrices(params);
    }

    private async loadPurchasePriceByServiceIds(serviceIds: string[]) {
        if (serviceIds.length > 0) {
            const chunkIds = getUniqChunkedIds(serviceIds);

            const purchasePrices = await Promise.all(
                chunkIds.map(ids => PriceApi.getPurchasePrices({ serviceId: ids })),
            );

            purchasePrices.forEach(price => {
                this.priceStore.purchasePriceList.add(price);
            });
        }
    }

    private async loadNextPrices(stopIndex = 0, isLegalEntityPriceList?: boolean): Promise<void> {
        const { priceList } = this.priceStore;

        if (!this.onLoadNext && priceList.needLoadNext(stopIndex)) {
            this.onLoadNext = true;
            const params = priceList.queryParams;
            const { prices } = await this.getPrices(params, isLegalEntityPriceList);
            await this.loadPriceRelatedData(prices);

            priceList.addNext(prices);

            this.onLoadNext = false;
        }
    }

    private async loadNextSearchPrices(
        stopIndex = 0,
        saveToAllLists: boolean = false,
        isLegalEntityPriceList?: boolean,
    ): Promise<void> {
        const { searchList, priceList } = this.priceStore;

        if (!this.onLoadNext && searchList.needLoadNext(stopIndex)) {
            this.onLoadNext = true;

            const { prices } = await this.getPrices(searchList.searchParams, isLegalEntityPriceList);
            await this.loadPriceRelatedData(prices);
            searchList.addNext(prices);

            if (saveToAllLists) {
                priceList.addIfNotExist(prices);
            }

            this.onLoadNext = false;
        }
    }

    private async loadPriceRelatedData(
        prices: Pick<PriceData, 'serviceId' | 'includedServices' | 'userId'>[],
    ): Promise<void> {
        const servicesIds = flatMap(prices, ({ serviceId, includedServices = [] }) => [serviceId, ...includedServices]);
        const userIds = compact(prices.map(({ userId }) => userId));

        await Promise.all([serviceService.loadByIds(servicesIds), userService.loadIfNotExist(userIds)]);
    }

    private async checkBasePrice(serviceId: string, legalEntityIds: string[]): Promise<ValidationParams> {
        const isValid = await this.hasBasePrice(serviceId, legalEntityIds);

        return {
            isValid,
            message: !isValid ? `${translate('needBasePriceFirst')}. ` : '',
        };
    }

    private async hasBasePrice(serviceId: string, legalEntityIds: string[]): Promise<boolean> {
        const basePrice = await this.getBasePrice(serviceId, legalEntityIds);

        return !!basePrice;
    }

    private async getBasePrice(serviceId: string, legalEntityIds: string[]): Promise<Optional<Price>> {
        let basePrice = this.priceStore.getBasePrice(serviceId, legalEntityIds);

        if (!basePrice) {
            const { prices } = await PriceApi.getPrices({
                serviceId,
                legalEntityIds: legalEntityIds.join(','),
                userId: 'null',
                status: [PriceStatus.Active, PriceStatus.Planned].join(),
            });

            const basePriceData = prices.find(
                price =>
                    price.serviceId === serviceId &&
                    isNil(price.userId) &&
                    price.legalEntityIds.length === legalEntityIds.length &&
                    price.legalEntityIds.every(id => legalEntityIds.includes(id)) &&
                    (price.status === PriceStatus.Active || price.status === PriceStatus.Planned),
            );

            basePrice = basePriceData && Price.fromData(basePriceData);
        }

        return basePrice;
    }

    private async archiveCustomPrices(serviceId: string, legalEntityIds: string[]): Promise<void> {
        const activeCustomPrices: PriceData[] = [];
        const plannedCustomPrices: PriceData[] = [];

        const customPrices = await this.getCustomPrices(serviceId, legalEntityIds);

        customPrices.forEach(customPrice => {
            if (customPrice.status === PriceStatus.Active) {
                activeCustomPrices.push(customPrice);
            } else if (customPrice.status === PriceStatus.Planned) {
                plannedCustomPrices.push(customPrice);
            }
        });

        await Promise.all([
            ...activeCustomPrices.map(customPrice =>
                PriceApi.updatePriceStatus(customPrice.id, { status: PriceStatus.Archived }),
            ),
            ...plannedCustomPrices.map(customPrice => PriceApi.deletePrice(customPrice.id)),
        ]);

        auditLogService.logEvents(
            activeCustomPrices.map(customPrice => ({
                eventType: AuditEventType.PriceStatusUpdate,
                entityId: customPrice.id,
                data: {
                    id: customPrice.id,
                    status: PriceStatus.Archived,
                },
            })),
        );
        this.archivedIfExist(activeCustomPrices);
        this.removePrices(plannedCustomPrices);
    }

    private async getCustomPrices(serviceId: string, legalEntityIds: string[]): Promise<PriceData[]> {
        const { prices } = await PriceApi.getPrices({
            serviceId,
            status: [PriceStatus.Active, PriceStatus.Planned].join(),
        });

        return prices.filter(
            price =>
                price.serviceId === serviceId &&
                price.status !== PriceStatus.Archived &&
                !isNil(price.userId) &&
                !isEmpty(intersection(price.legalEntityIds, legalEntityIds)),
        );
    }

    private archivedIfExist(prices: PriceData[]): void {
        prices.forEach(({ id }) => {
            const price = this.priceStore.getPrice(id);
            if (price) {
                price.update({ status: PriceStatus.Archived });
            }
        });
    }

    private removePrices(prices: PriceData[]): void {
        prices.forEach(({ id }) => {
            this.priceStore.delete(id);
        });
    }

    private checkUserPriceParams({ userId }: Partial<PriceCreationParams>): ValidationParams {
        const isValid = Boolean(userId);

        return {
            isValid,
            message: !isValid ? `${translate('cantLinkUserWithoutOrganization')}. ` : '',
        };
    }

    private checkRequiredParams({ serviceId, scheduledOn }: Partial<PriceCreationParams>): ValidationParams {
        const isValid = !!serviceId && !!scheduledOn;
        const emptyFields: string[] = [];

        if (!serviceId) {
            emptyFields.push(translate('serviceGenitive'));
        }
        if (!scheduledOn) {
            emptyFields.push(translate('activationDate'));
        }

        return {
            isValid,
            message: !isValid ? `${translate('mustSpecify')}: ${emptyFields.join(', ')}. ` : '',
        };
    }

    private getLegalEntityPriceListId(): string {
        const { legalEntityPriceListId } = this.priceStore;
        assertIsDefined(legalEntityPriceListId, 'Legal entity price list prices are loaded only for a set price list');
        return legalEntityPriceListId;
    }
}
